import { AxiosError } from 'axios';

import {
  ServerActionPopupErrorStatus,
  ServerAuthErrorStatus,
  ServerPageErrorStatus,
  ServerPopupErrorStatus
} from '@/constants/error/serverErrorStatus';
import { ServerActionPopupErrorBody } from '@/utils/error/ServerActionPopupError';

type ServerAuthErrorBody = {
  status: ServerAuthErrorStatus;
  message: string;
};

type ServerPageErrorBody = {
  status: ServerPageErrorStatus;
  message: string;
};

type ServerPopupErrorBody = {
  status: ServerPopupErrorStatus;
  message: string;
};

type ErrorBody = ServerAuthErrorBody | ServerPageErrorBody | ServerPopupErrorBody | ServerActionPopupErrorBody;
type ErrorStatus =
  | typeof ServerAuthErrorStatus
  | typeof ServerPageErrorStatus
  | typeof ServerPopupErrorStatus
  | typeof ServerActionPopupErrorStatus;

abstract class HttpStatusError extends AxiosError {
  abstract status: number;

  static hasStatus(errorResponseBody: unknown): errorResponseBody is ErrorBody {
    return typeof errorResponseBody === 'object' && errorResponseBody !== null && 'status' in errorResponseBody;
  }

  static include(errorStatus: ErrorStatus, status: number): boolean {
    return Object.values(errorStatus).includes(status);
  }

  static isServerPopupError(errorResponseBody: unknown): errorResponseBody is ServerPopupErrorBody {
    return this.hasStatus(errorResponseBody) && this.include(ServerPopupErrorStatus, errorResponseBody.status);
  }

  static isServerActionPopupError(errorResponseBody: unknown): errorResponseBody is ServerActionPopupErrorBody {
    return this.hasStatus(errorResponseBody) && this.include(ServerActionPopupErrorStatus, errorResponseBody.status);
  }

  static isServerPageError(errorResponseBody: unknown): errorResponseBody is ServerPageErrorBody {
    return this.hasStatus(errorResponseBody) && this.include(ServerPageErrorStatus, errorResponseBody.status);
  }

  static isServerAuthError(errorResponseBody: unknown): errorResponseBody is ServerAuthErrorBody {
    return this.hasStatus(errorResponseBody) && this.include(ServerAuthErrorStatus, errorResponseBody.status);
  }
}

export default HttpStatusError;
